import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Mail, Phone, Tag, MapPin, Clock, Home, Search, FileCheck, Landmark, CalendarClock, Plus } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { errorMessage } from '../errors';
import { reApi } from '../workspacesApi';
import { useAuth } from '../AuthContext';
import { AssignmentPicker } from '../loan-file/shared/AssignmentPicker';
import { uc } from '../unifiedContactsApi';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import Board from './Board';
import BoardCard from './BoardCard';
import CardDrawer from './CardDrawer';
import AlertsStrip from './AlertsStrip';
import CreateREOpportunityDialog from './CreateREOpportunityDialog';
import OpportunityEditSection from './OpportunityEditSection';
import LoadMoreBar from './LoadMoreBar';
import { RE_CLOSED_STAGES } from './oppFields';
import { PAGE_LIMIT, appendNew, firstPage, nextPage } from './paging';
import { addressLine } from '../shared/address';
import { formatPhone } from '../shared/phone';
import { formatDate } from '../kit/time';

const ALERT_DAYS = 14;
const SIDES = ['buyer', 'seller'];
const fmt$ = (n) => n ? Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }) : '—';
const primary = (list) => (list || []).find(x => x.is_primary)?.value || list?.[0]?.value || null;
const byOrder = (list) => (list || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
const cap = (s) => s ? s[0].toUpperCase() + s.slice(1) : s;

const cardId = (o) => o.opportunity_id;
const cardStage = (o) => o.stage;

// Card assignments (ticket 69) — the loan-header picker pointed at the opportunity.
const oppAssignments = {
  list: (id) => reApi.listAssignments(id),
  add: (id, body) => reApi.addAssignment(id, body),
  remove: (id, userId, slot) => reApi.removeAssignment(id, userId, slot),
};

// The six deadline categories GET /api/v2/re/alerts returns. Listing alerts
// come from re_opportunities; the other five from re_transactions, so they
// identify by transaction_id and are matched to a card via opp.transaction_id.
const RE_ALERT_GROUPS = [
  { key: 'listing_expiring', title: 'Listing Expiring', icon: Clock, field: 'expires_at' },
  { key: 'option_expiring', title: 'Option Expiring', icon: CalendarClock, field: 'expires_at', idField: 'transaction_id' },
  { key: 'inspection_soon', title: 'Inspection', icon: Search, field: 'date', idField: 'transaction_id' },
  { key: 'appraisal_soon', title: 'Appraisal', icon: FileCheck, field: 'date', idField: 'transaction_id' },
  { key: 'financing_soon', title: 'Financing', icon: Landmark, field: 'date', idField: 'transaction_id' },
  { key: 'closing_soon', title: 'Closing', icon: Home, field: 'date', idField: 'transaction_id' },
];

// Price shown on a card / drawer banner: sellers list, buyers shop up to a cap.
const priceOf = (o) => o.side === 'seller' ? o.listing_price : (o.target_price_max || o.approved_amount);
const priceLabel = (o) => o.side === 'seller' ? 'Listing Price' : 'Target Max';
const whereOf = (o) => addressLine(o.property_address) || (o.target_areas || []).join(', ') || null;

// Real-estate flavor of the shared Board: buyer/seller side toggle, one stage
// set per side from the v2 RE stages endpoint, cards from re_opportunities,
// client names from one batch contacts_v2 lookup. Hosts the six-category
// deadline alerts strip (from the retired RE Workspace page).
export default function REBoard({ initialOpenId } = {}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canAssign = (user?.permissions || []).includes('loans.assign');
  const [side, setSide] = useState('buyer');
  const [stagesBySide, setStagesBySide] = useState({ buyer: [], seller: [] });
  const [opps, setOpps] = useState([]);
  const [contacts, setContacts] = useState({});      // contact_id -> contact
  const [alerts, setAlerts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);        // drawer open request (alerts)
  const [creating, setCreating] = useState(false);
  const initialOpenApplied = useRef(false);
  const [page, setPage] = useState({ loaded: 0, total: 0, cursor: null });             // open list paging (ticket 187)
  const [closedFilter, setClosedFilter] = useState('open');             // 'open' or a closed stage key
  const [closedOpps, setClosedOpps] = useState([]);
  const [closedPage, setClosedPage] = useState({ loaded: 0, total: 0, cursor: null });
  const [closedKey, setClosedKey] = useState(null);   // 'side:stage' the closed list was loaded for
  const [loadingMore, setLoadingMore] = useState(false);
  const [pinned, setPinned] = useState(null);   // a closed card opened by link, shown even when the closed page lacks it

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [buyerDoc, sellerDoc, oppDoc, alertDoc] = await Promise.all([
        reApi.getStages('buyer'),
        reApi.getStages('seller'),
        reApi.listOpps({ limit: PAGE_LIMIT }),
        reApi.alerts(ALERT_DAYS).catch(() => null),
      ]);
      const items = oppDoc.items || [];
      const ids = [...new Set(items.map(o => o.contact_id).filter(Boolean))];
      const people = await uc.byIds(ids).catch(() => []);
      setStagesBySide({ buyer: byOrder(buyerDoc.stages), seller: byOrder(sellerDoc.stages) });
      setOpps(items);
      setPage(firstPage(items, oppDoc));
      setContacts(Object.fromEntries(people.map(p => [p.contact_id, p])));
      setAlerts(alertDoc);
    } catch (e) {
      toast({ title: 'Load failed', description: errorMessage(e) });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Closed view (ticket 187), mirroring the mortgage board's closed filter: one view per terminal
  // stage the side has. Switching side drops a view the new side does not have.
  const closedOptions = useMemo(
    () => stagesBySide[side].filter(st => RE_CLOSED_STAGES.includes(st.key)),
    [stagesBySide, side],
  );
  const view = closedFilter !== 'open' && closedOptions.some(st => st.key === closedFilter) ? closedFilter : 'open';
  const closedLoading = view !== 'open' && closedKey !== `${side}:${view}`;

  // Closed cards are fetched separately since the open list excludes them; the server filters by
  // side and stage, so `total` is the size of this view.
  useEffect(() => {
    if (view === 'open') return undefined;
    let cancelled = false;
    reApi.listOpps({ limit: PAGE_LIMIT, closed_only: true, side, stage: view })
      .then(async (doc) => {
        const items = doc.items || [];
        const ids = [...new Set(items.map(o => o.contact_id).filter(Boolean))];
        const people = await uc.byIds(ids).catch(() => []);
        if (cancelled) return;
        setClosedOpps(items);
        setClosedPage(firstPage(items, doc));
        setContacts(prev => ({ ...prev, ...Object.fromEntries(people.map(p => [p.contact_id, p])) }));
        setClosedKey(`${side}:${view}`);
      })
      .catch((e) => {
        if (cancelled) return;
        toast({ title: 'Load failed', description: errorMessage(e) });
        setClosedOpps([]);
        setClosedPage({ loaded: 0, total: 0, cursor: null });
        setClosedKey(`${side}:${view}`);
      });
    return () => { cancelled = true; };
  }, [view, side]);

  // Open a card's drawer by id (alerts strip, deep link), switching to its side. A card the open
  // list does not hold is fetched: an open one (past the page cap) joins the board; a closed one
  // switches to its closed view, pinned so it shows even when the first page of closed cards
  // does not include it.
  const openById = useCallback(async (id) => {
    const found = opps.find(o => o.opportunity_id === id);
    if (found) {
      setSide(found.side || 'buyer');
      setOpenId(id);
      return;
    }
    try {
      const o = await reApi.getOpp(id);
      const oSide = o.side || 'buyer';
      if (o.contact_id && !contacts[o.contact_id]) {
        const people = await uc.byIds([o.contact_id]).catch(() => []);
        if (people[0]) setContacts(prev => ({ ...prev, [people[0].contact_id]: people[0] }));
      }
      if (o.is_closed) {
        if (!stagesBySide[oSide].some(st => st.key === o.stage && RE_CLOSED_STAGES.includes(st.key))) {
          throw new Error('no closed view for this stage');
        }
        setPinned(o);
        setClosedFilter(o.stage);
      } else {
        setOpps(prev => appendNew(prev, [o]));
        setClosedFilter('open');
      }
      setSide(oSide);
      setOpenId(id);
    } catch {
      toast({ title: 'Not on the board', description: 'That opportunity is no longer in the pipeline.' });
    }
  }, [opps, contacts, stagesBySide]);

  // Deep link from a contact's opportunity list (PipelinePage's initialOpenId) —
  // apply once, after the initial load, so a missing card gets a single toast.
  useEffect(() => {
    if (!initialOpenId || initialOpenApplied.current || loading) return;
    initialOpenApplied.current = true;
    openById(initialOpenId);
  }, [initialOpenId, loading, openById]);

  const columns = useMemo(() => stagesBySide[side].filter(s => !s.hidden), [stagesBySide, side]);
  const cards = useMemo(() => opps.filter(o => (o.side || 'buyer') === side), [opps, side]);
  const closedCards = useMemo(() => {
    const here = pinned && pinned.is_closed && pinned.stage === view && (pinned.side || 'buyer') === side;
    return here ? appendNew(closedOpps, [pinned]) : closedOpps;
  }, [closedOpps, pinned, view, side]);
  const closedColumns = useMemo(
    () => [{ key: view, label: closedOptions.find(st => st.key === view)?.label || view, color: '#94A3B8' }],
    [view, closedOptions],
  );
  const sideCounts = useMemo(() => {
    const n = { buyer: 0, seller: 0 };
    for (const o of opps) n[o.side || 'buyer'] = (n[o.side || 'buyer'] || 0) + 1;
    return n;
  }, [opps]);

  const nameOf = useCallback((o) => contacts[o.contact_id]?.display_name || 'Unknown client', [contacts]);
  // Persist the move, then mirror it into `opps` so the side toggle (which
  // re-derives the Board's cards) doesn't snap the card back to its old column.
  const onMove = useCallback(async (o, toStage) => {
    const r = await reApi.moveStage(o.opportunity_id, toStage);
    setOpps(prev => prev.map(x => x.opportunity_id === o.opportunity_id ? { ...x, stage: toStage } : x));
    return r;
  }, []);

  // Edit saved from the drawer: fold the saved fields into the card, wherever it is held, and
  // refresh the alerts, since the form is what sets the listing-expiration date they read.
  const onOppSaved = useCallback((updated) => {
    const merge = (list) => list.map(x => x.opportunity_id === updated.opportunity_id ? { ...x, ...updated } : x);
    setOpps(merge);
    setClosedOpps(merge);
    setPinned(p => (p && p.opportunity_id === updated.opportunity_id ? { ...p, ...updated } : p));
    reApi.alerts(ALERT_DAYS).then(setAlerts).catch(() => {});
  }, []);

  // Create: prepend the new card, resolve its client name without reloading the
  // whole board, and switch the toggle to the created side so the card is visible
  // even if it differs from what was showing when the dialog opened.
  const onCreated = useCallback(async (opp, contact) => {
    setOpps(prev => [opp, ...prev]);
    setPage(p => ({ ...p, loaded: p.loaded + 1, total: p.total + 1 }));
    setClosedFilter('open');
    if (contact) {
      setContacts(prev => ({ ...prev, [contact.contact_id]: contact }));
    } else if (opp.contact_id && !contacts[opp.contact_id]) {
      const people = await uc.byIds([opp.contact_id]).catch(() => []);
      if (people[0]) setContacts(prev => ({ ...prev, [people[0].contact_id]: people[0] }));
    }
    setSide(opp.side || 'buyer');
  }, [contacts]);

  // An alert names either an opportunity (listing) or a transaction. Open the
  // matching card's drawer — switching side if needed — else fall through to
  // the transaction page for under-contract deadlines with no linked card.
  const openFromAlert = useCallback((item) => {
    const opp = item.opportunity_id
      ? opps.find(o => o.opportunity_id === item.opportunity_id)
      : opps.find(o => o.transaction_id && o.transaction_id === item.transaction_id);
    if (opp) {
      setSide(opp.side || 'buyer');
      setOpenId(opp.opportunity_id);
    } else if (item.opportunity_id) {
      openById(item.opportunity_id);
    } else if (item.transaction_id) {
      navigate(`/portal/real-estate/transactions/${item.transaction_id}`);
    } else {
      toast({ title: 'Not on the board', description: 'That record is closed or no longer in the pipeline.' });
    }
  }, [opps, navigate, openById]);

  // Next page of the open or closed list, with the client names it needs.
  const loadMore = useCallback(async () => {
    const closed = view !== 'open';
    const cur = closed ? closedPage : page;
    setLoadingMore(true);
    try {
      const doc = await reApi.listOpps({ limit: PAGE_LIMIT, cursor: cur.cursor, ...(closed ? { closed_only: true, side, stage: view } : {}) });
      const items = doc.items || [];
      const ids = [...new Set(items.map(o => o.contact_id).filter(id => id && !contacts[id]))];
      const people = await uc.byIds(ids).catch(() => []);
      setContacts(prev => ({ ...prev, ...Object.fromEntries(people.map(p => [p.contact_id, p])) }));
      if (closed) {
        setClosedOpps(prev => appendNew(prev, items));
        setClosedPage(nextPage(cur, items, doc));
      } else {
        setOpps(prev => appendNew(prev, items));
        setPage(nextPage(cur, items, doc));
      }
    } catch (e) {
      toast({ title: 'Load failed', description: errorMessage(e) });
    } finally { setLoadingMore(false); }
  }, [view, side, closedPage, page, contacts]);

  const renderCard = useCallback((o) => (
    <BoardCard
      product="real_estate"
      avatar={nameOf(o)[0]?.toUpperCase()}
      title={nameOf(o)}
      subtitle={whereOf(o)}
      footerLeft={cap(o.side || 'buyer')}
      footerRight={o.side === 'seller' ? fmt$(priceOf(o)) : (priceOf(o) ? `Up to ${fmt$(priceOf(o))}` : '—')}
    />
  ), [nameOf]);

  const renderDrawer = useCallback(({ card: o, stage, onClose, onChangeStage }) => {
    const c = contacts[o.contact_id];
    const email = primary(c?.emails);
    const phone = primary(c?.phones);
    return (
      <CardDrawer
        eyebrow={`${cap(o.side || 'buyer')} Opportunity`}
        title={nameOf(o)}
        amountLabel={priceLabel(o)}
        amount={priceOf(o)}
        columns={view === 'open' ? columns : closedColumns}
        currentStage={stage}
        onChangeStage={onChangeStage}
        onClose={onClose}
        openFull={o.transaction_id
          ? { label: 'Open full transaction', onClick: () => navigate(`/portal/real-estate/transactions/${o.transaction_id}`) }
          : {
              label: 'Create transaction',
              onClick: async () => {
                try {
                  const r = await reApi.createTransactionFromOpp(o.opportunity_id);
                  await load();
                  navigate(`/portal/real-estate/transactions/${r.transaction.transaction_id}`);
                } catch (err) {
                  if (err?.response?.data?.detail?.code === 'already_linked') {
                    navigate(`/portal/real-estate/transactions/${err.response.data.detail.transaction_id}`);
                  } else {
                    const d = err?.response?.data?.detail;
                    toast({ title: 'Could not create transaction', description: typeof d === 'string' ? d : (d?.message || d?.code || String(err)) });
                  }
                }
              },
            }}
      >
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Contact</div>
          {email && <div className="flex items-center gap-2 text-sm text-slate-700 mb-1" data-testid="drawer-contact-email"><Mail size={13} className="text-slate-400" /> {email}</div>}
          {phone && <div className="flex items-center gap-2 text-sm text-slate-700" data-testid="drawer-contact-phone"><Phone size={13} className="text-slate-400" /> {formatPhone(phone)}</div>}
          {!email && !phone && <div className="text-sm text-slate-400 italic">No contact info yet.</div>}
        </div>
        <OpportunityEditSection kind="re" opp={o} onSaved={onOppSaved}>
          <div className="space-y-1 text-sm">
            <div className="flex items-center gap-2 text-slate-700"><Tag size={13} className="text-slate-400" /> Side: <b className="capitalize" style={{ color: '#070B5F' }} data-testid="drawer-side">{o.side || 'buyer'}</b></div>
            {whereOf(o) && <div className="flex items-center gap-2 text-slate-700"><MapPin size={13} className="text-slate-400" /> {o.side === 'seller' ? 'Property' : 'Areas'}: <b style={{ color: '#070B5F' }}>{whereOf(o)}</b></div>}
            {o.mls_number && <div className="text-slate-700">MLS: <b style={{ color: '#070B5F' }}>{o.mls_number}</b></div>}
            {o.listing_expiration && <div className="text-slate-700">Listing expires: <b style={{ color: '#070B5F' }}>{formatDate(o.listing_expiration)}</b></div>}
            {o.preapproval_status && <div className="text-slate-700">Preapproval: <b className="capitalize" style={{ color: '#070B5F' }}>{o.preapproval_status}</b></div>}
            {o.side !== 'seller' && (o.target_price_min || o.target_price_max) && (
              <div className="text-slate-700" data-testid="drawer-budget">Budget: <b style={{ color: '#070B5F' }}>{[o.target_price_min ? fmt$(o.target_price_min) : null, o.target_price_max ? fmt$(o.target_price_max) : null].filter(Boolean).join(' to ')}</b></div>
            )}
            {o.approved_amount ? <div className="text-slate-700">Approved amount: <b style={{ color: '#070B5F' }}>{fmt$(o.approved_amount)}</b></div> : null}
            {o.transaction_id && <div className="text-slate-700">Transaction: <b style={{ color: '#070B5F' }} data-testid="drawer-transaction-id">{o.transaction_id}</b></div>}
            {o.notes && <div className="text-slate-700 whitespace-pre-wrap" data-testid="drawer-notes">Notes: <b style={{ color: '#070B5F' }}>{o.notes}</b></div>}
          </div>
        </OpportunityEditSection>
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Team</div>
          <AssignmentPicker loanId={o.opportunity_id} client={oppAssignments} canAssign={canAssign} title="Assign to this deal" />
        </div>
      </CardDrawer>
    );
  }, [contacts, columns, closedColumns, view, nameOf, navigate, canAssign, load, onOppSaved]);

  const alertLabel = useCallback((i) => (i.contact_id ? contacts[i.contact_id]?.display_name : null), [contacts]);

  if (loading) {
    return (
      <div className="p-16 flex items-center justify-center text-slate-400" data-testid="pipeline-loading">
        <Loader2 className="animate-spin mr-2" size={20} /> Loading pipeline...
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4" data-testid="pipeline-toolbar">
        <div className="inline-flex rounded-full border border-slate-200 overflow-hidden p-0.5 bg-slate-50" data-testid="pipeline-side-toggle">
          {SIDES.map(s => (
            <button key={s} type="button" onClick={() => setSide(s)} data-testid={`pipeline-side-${s}`}
              aria-pressed={side === s}
              className="px-3 py-1.5 text-xs font-semibold rounded-full transition-colors"
              style={side === s ? { background: '#070B5F', color: '#fff' } : { color: '#64748b' }}>
              {cap(s)} · {sideCounts[s] || 0}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Select value={view} onValueChange={setClosedFilter}>
            <SelectTrigger className="h-9 w-40" data-testid="pipeline-closed-filter" aria-label="Closed deals">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Open deals</SelectItem>
              {closedOptions.map(st => <SelectItem key={st.key} value={st.key}>{st.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <button onClick={() => setCreating(true)} className="btn-gold" data-testid="pipeline-new-opp-btn">
            <Plus size={16} /> New Opportunity
          </button>
        </div>
      </div>

      {view === 'open' ? (
        <>
          <AlertsStrip alerts={alerts} days={ALERT_DAYS} product="real_estate" groups={RE_ALERT_GROUPS} noun="deadline" labelFor={alertLabel} onOpen={openFromAlert} />
          <LoadMoreBar shown={page.loaded} total={page.total} hasMore={!!page.cursor} noun="open opportunities" loading={loadingMore} onMore={loadMore} />
          <Board
            columns={columns}
            cards={cards}
            cardId={cardId}
            cardStage={cardStage}
            cardTitle={nameOf}
            onMove={onMove}
            renderCard={renderCard}
            renderDrawer={renderDrawer}
            emptyMsg={`Drop ${side} opportunities here`}
            testIdPrefix="pipeline"
            openId={openId}
            onOpenChange={setOpenId}
          />
        </>
      ) : closedLoading ? (
        <div className="p-16 flex items-center justify-center text-slate-400" data-testid="pipeline-closed-loading">
          <Loader2 className="animate-spin mr-2" size={20} /> Loading...
        </div>
      ) : (
        <>
          <LoadMoreBar shown={closedPage.loaded} total={closedPage.total} hasMore={!!closedPage.cursor} noun={`${closedColumns[0].label.toLowerCase()} opportunities`} loading={loadingMore} onMore={loadMore} />
          <Board
            columns={closedColumns}
            cards={closedCards}
            cardId={cardId}
            cardStage={cardStage}
            cardTitle={nameOf}
            onMove={async () => {}}
            renderCard={renderCard}
            renderDrawer={renderDrawer}
            emptyMsg={`No ${closedColumns[0].label.toLowerCase()} ${side} opportunities`}
            testIdPrefix="pipeline-closed"
            openId={openId}
            onOpenChange={setOpenId}
          />
        </>
      )}

      <CreateREOpportunityDialog open={creating} onOpenChange={setCreating} defaultSide={side} onCreated={onCreated} stagesBySide={stagesBySide} />
    </>
  );
}
